import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuickBooksSync, QbEstimateDiff } from "../hooks/useQuickBooksSync";
import CloseIcon from "@mui/icons-material/Close";

interface QuickBooksEstimateDiffDialogProps {
  quoteId: number;
  // The draft quote this quote was finalized from, if any — work orders are
  // built from the draft's own objects/measurements, not the finalized
  // quote's flat qty/price lines, so a QBO-side edit (e.g. a width changed,
  // not just a price) can't be reconciled onto them automatically. "Apply
  // Changes Manually" sends the user there instead of trying to guess.
  draftQuoteId: number | null;
  onClose: () => void;
  onApplied?: () => void;
  // Set when the quote detail page already ran the diff itself (to decide
  // whether to auto-open this dialog at all) — skips this dialog's own
  // fetch-on-mount so it opens instantly with that result instead of
  // showing a loading state for a check that already happened.
  initialDiff?: QbEstimateDiff;
}

const dialogOverlayStyle: React.CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};

const dialogStyle: React.CSSProperties = {
  backgroundColor: "white",
  borderRadius: "8px",
  boxShadow: "0 20px 25px rgba(0, 0, 0, 0.15)",
  maxWidth: "800px",
  width: "95%",
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
};

const dialogHeaderStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "24px",
  borderBottom: "1px solid #e5e7eb",
};

const closeButtonStyle: React.CSSProperties = {
  background: "none",
  border: "none",
  fontSize: "24px",
  cursor: "pointer",
  color: "#6b7280",
};

const dialogContentStyle: React.CSSProperties = {
  flex: 1,
  overflow: "auto",
  padding: "24px",
};

const dialogFooterStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "12px",
  padding: "16px 24px",
  borderTop: "1px solid #e5e7eb",
};

const submitButtonStyle: React.CSSProperties = {
  padding: "10px 24px",
  backgroundColor: "#10b981",
  color: "white",
  border: "none",
  borderRadius: "6px",
  fontSize: "14px",
  fontWeight: 600,
  cursor: "pointer",
};

const cancelButtonStyle: React.CSSProperties = {
  padding: "10px 24px",
  backgroundColor: "#f3f4f6",
  color: "#374151",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  fontSize: "14px",
  fontWeight: 600,
  cursor: "pointer",
};

const manualButtonStyle: React.CSSProperties = {
  padding: "10px 24px",
  backgroundColor: "white",
  color: "#3b82f6",
  border: "1px solid #93c5fd",
  borderRadius: "6px",
  fontSize: "14px",
  fontWeight: 600,
  cursor: "pointer",
};

const errorStyle: React.CSSProperties = {
  padding: "12px",
  backgroundColor: "#fee2e2",
  color: "#dc2626",
  borderRadius: "6px",
  fontSize: "13px",
  marginBottom: "16px",
};

const sectionTitleStyle = (color: string): React.CSSProperties => ({
  fontSize: "13px",
  fontWeight: 700,
  color,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  marginBottom: "8px",
  marginTop: "20px",
});

const rowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  gap: "12px",
  padding: "8px 12px",
  borderRadius: "6px",
  fontSize: "13px",
  marginBottom: "6px",
};

const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const fmtQty = (qty: number) =>
  Number.isInteger(qty) ? String(qty) : qty.toFixed(2);

export default function QuickBooksEstimateDiffDialog({
  quoteId,
  draftQuoteId,
  onClose,
  onApplied,
  initialDiff,
}: QuickBooksEstimateDiffDialogProps) {
  const navigate = useNavigate();
  const { fetchEstimateDiff, applyEstimateDiff, isPending } =
    useQuickBooksSync();
  const [diff, setDiff] = useState<QbEstimateDiff | null>(initialDiff ?? null);
  const [loading, setLoading] = useState(!initialDiff);
  const [error, setError] = useState<string | null>(null);
  const [resultMessage, setResultMessage] = useState<string | null>(null);

  useEffect(() => {
    if (initialDiff) return; // already have a result — skip the redundant fetch
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const result = await fetchEstimateDiff(quoteId);
      if (cancelled) return;
      setLoading(false);
      if (!result.ok) {
        setError(result.message);
        return;
      }
      setDiff(result.diff || null);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId]);

  const hasChanges =
    !!diff &&
    (diff.removed.length > 0 ||
      diff.changed.length > 0 ||
      diff.added.length > 0);

  const handleApply = async () => {
    setError(null);
    setResultMessage(null);
    const result = await applyEstimateDiff(quoteId);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    setResultMessage(result.message);
    onApplied?.();
    onClose();
  };

  const handleApplyManually = () => {
    if (!draftQuoteId) return;
    onClose();
    navigate(`/draft-quotes/${draftQuoteId}`);
  };

  return (
    <div style={dialogOverlayStyle}>
      <div style={dialogStyle}>
        <div style={dialogHeaderStyle}>
          <h2
            style={{
              margin: 0,
              fontSize: "20px",
              color: "#111827",
              fontWeight: 700,
            }}
          >
            Pull Estimate from QuickBooks
          </h2>
          <button style={closeButtonStyle} onClick={onClose} title="Close">
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div style={dialogContentStyle}>
          {error && <div style={errorStyle}>{error}</div>}
          {resultMessage && (
            <div
              style={{
                ...errorStyle,
                backgroundColor: "#d1fae5",
                color: "#047857",
              }}
            >
              {resultMessage}
            </div>
          )}

          {loading ? (
            <div className="loading-message">
              Comparing against QuickBooks...
            </div>
          ) : diff && !hasChanges ? (
            <div style={{ fontSize: "14px", color: "#6b7280" }}>
              No differences — this quote already matches what's in QuickBooks (
              {diff.unchangedCount} line{diff.unchangedCount === 1 ? "" : "s"}{" "}
              checked).
            </div>
          ) : diff ? (
            <>
              {diff.removed.length > 0 && (
                <>
                  <div style={sectionTitleStyle("#dc2626")}>
                    Only in d2x — should be removed ({diff.removed.length})
                  </div>
                  <p
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      margin: "0 0 8px",
                    }}
                  >
                    These lines aren't in QuickBooks anymore and should be
                    archived from the d2x quote.
                  </p>
                  {diff.removed.map((row) => (
                    <div
                      key={row.id}
                      style={{ ...rowStyle, backgroundColor: "#fef2f2" }}
                    >
                      <span>{row.description}</span>
                      <span style={{ color: "#6b7280", whiteSpace: "nowrap" }}>
                        {fmtQty(row.quantity)} @ {fmt(row.unitPriceCents)}
                      </span>
                    </div>
                  ))}
                </>
              )}

              {diff.changed.length > 0 && (
                <>
                  <div style={sectionTitleStyle("#b45309")}>
                    Changed in QuickBooks ({diff.changed.length})
                  </div>
                  <p
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      margin: "0 0 8px",
                    }}
                  >
                    These lines' quantity or price should be updated to match
                    QuickBooks.
                  </p>
                  {diff.changed.map((row) => (
                    <div
                      key={row.id}
                      style={{
                        ...rowStyle,
                        backgroundColor: "#fffbeb",
                        flexDirection: "column",
                        alignItems: "flex-start",
                      }}
                    >
                      <span style={{ fontWeight: 600 }}>{row.description}</span>
                      <span style={{ color: "#6b7280" }}>
                        {fmtQty(row.localQuantity)} @{" "}
                        {fmt(row.localUnitPriceCents)}
                        {" → "}
                        <strong style={{ color: "#111827" }}>
                          {fmtQty(row.qbQuantity)} @ {fmt(row.qbUnitPriceCents)}
                        </strong>
                      </span>
                    </div>
                  ))}
                </>
              )}

              {diff.added.length > 0 && (
                <>
                  <div style={sectionTitleStyle("#047857")}>
                    Only in QuickBooks — should be added ({diff.added.length})
                  </div>
                  <p
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      margin: "0 0 8px",
                    }}
                  >
                    These lines exist in QuickBooks but not on this quote —
                    they'll be added under "Other."
                  </p>
                  {diff.added.map((row) => (
                    <div
                      key={row.qbLineId}
                      style={{ ...rowStyle, backgroundColor: "#ecfdf5" }}
                    >
                      <span>{row.description}</span>
                      <span style={{ color: "#6b7280", whiteSpace: "nowrap" }}>
                        {fmtQty(row.quantity)} @ {fmt(row.unitPriceCents)}
                      </span>
                    </div>
                  ))}
                </>
              )}

              <div
                style={{
                  marginTop: "20px",
                  padding: "10px 12px",
                  backgroundColor: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  borderRadius: "6px",
                  fontSize: "12px",
                  color: "#1e40af",
                }}
              >
                Use <strong>Apply Changes to Draft Object</strong> if this
                change affects measurements — work orders are built from the{" "}
                <em>draft</em> quote, not the finalized one.{" "}
                <strong>Overwrite d2x Quote</strong> only updates this finalized
                quote's line items and totals.
              </div>
            </>
          ) : null}
        </div>

        <div style={dialogFooterStyle}>
          <span style={{ fontSize: "13px", color: "#6b7280" }}>
            {diff &&
              hasChanges &&
              `${diff.removed.length + diff.changed.length + diff.added.length} change(s) found`}
          </span>
          <div style={{ display: "flex", gap: "12px" }}>
            <button
              style={cancelButtonStyle}
              onClick={onClose}
              disabled={isPending(quoteId)}
            >
              {hasChanges ? "Cancel" : "Close"}
            </button>
            {hasChanges && (
              <>
                <button
                  style={manualButtonStyle}
                  onClick={handleApply}
                  disabled={isPending(quoteId)}
                >
                  {isPending(quoteId) ? "Applying..." : "Overwrite d2x Quote"}
                </button>
                <button
                  style={submitButtonStyle}
                  onClick={handleApplyManually}
                  disabled={isPending(quoteId) || !draftQuoteId}
                  title={
                    draftQuoteId
                      ? undefined
                      : "No draft quote found for this quote"
                  }
                >
                  Apply Changes to Draft Object
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
